import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { AppThunk, createAppAsyncThunk } from "appThunk";
import { DateTime } from "luxon";
import { DataWrapper } from "domain/dataWrapper";
import { setupCube } from "modules/helpers/cube/cubeSlice";
import { notifyError } from "modules/notifications/notificationsSlice";
import { RootState } from "store";
import _, { maxBy } from "lodash";

import { MonthlyCosts, loadMonthlyCosts } from "./monthlyCosts";
import { MonthlyRevenue, loadMonthlyRevenue } from "./monthlyRevenue";
import {
    selectCostTypes,
    selectStoreGroups,
    selectRegions,
    selectClientRegions,
    selectReferenceDate,
    selectCostReductionOpportunityByStore,
    selectUseCustomCalendar
} from "modules/customer/insights/cost/costSlice";

import mathUtils from "utils/mathUtils";
import { CostTypeMultiSelect } from "modules/customer/insights/cost/costType";
import { StoreGroupMultiSelect } from "modules/customer/insights/cost/storeGroups";
import { RegionMultiSelect } from "modules/customer/insights/cost/regions";
import { CustomDate } from "modules/customer/insights/cost/customDate";
import { CostMeasureType } from "modules/customer/insights/cost/costMeasureType";
import { CostGranularity } from "modules/customer/insights/cost/costGranularity";
import { numberSortExpression, SortDirection } from "utils/sortUtils";

interface CostOverviewState {
    isLoading: boolean,
    hasErrors: boolean,
    monthlyCosts: MonthlyCosts[],
    monthlyRevenue: MonthlyRevenue[],
    costOverTimeCostTypes: CostTypeMultiSelect[],
    costMeasureType: CostMeasureType,
    costReductionOpportunitiesCostTypes: CostTypeMultiSelect[],
    costReductionOpportunitiesStoreGroups: StoreGroupMultiSelect[],
    costReductionOpportunitiesRegions: RegionMultiSelect[],
    costOverTimeGranularity: CostGranularity
}

const initialState: CostOverviewState = {
    isLoading: false,
    hasErrors: false,
    monthlyCosts: [],
    monthlyRevenue: [],
    costOverTimeCostTypes: [],
    costMeasureType: CostMeasureType.CostValue,
    costReductionOpportunitiesCostTypes: [],
    costReductionOpportunitiesStoreGroups: [],
    costReductionOpportunitiesRegions: [],
    costOverTimeGranularity: CostGranularity.Month
};

interface LoadCostOverviewResponse {
    monthlyCosts: MonthlyCosts[],
    monthlyRevenue: MonthlyRevenue[],
    costOverTimeCostTypes: CostTypeMultiSelect[],
    costReductionOpportunitiesCostTypes: CostTypeMultiSelect[],
    costReductionOpportunitiesStoreGroups: StoreGroupMultiSelect[],
    costReductionOpportunitiesRegions: RegionMultiSelect[]
}

const costOverviewSlice = createSlice({
    name: "customer/inisghts/cost/costOverview",
    initialState,
    reducers: {
        clearMonthlyCosts: (state) => {
            state.monthlyCosts = initialState.monthlyCosts;
        },
        clearMonthlyRevenue: (state) => {
            state.monthlyRevenue = initialState.monthlyRevenue;
        },
        clearCostOverTimeCostTypes: (state) => {
            state.costOverTimeCostTypes = initialState.costOverTimeCostTypes;
        },
        clearCostReductionOpportunitiesCostTypes: (state) => {
            state.costReductionOpportunitiesCostTypes = initialState.costReductionOpportunitiesCostTypes;
        },
        clearCostReductionOpportunitiesStoreGroups: (state) => {
            state.costReductionOpportunitiesStoreGroups = initialState.costReductionOpportunitiesStoreGroups;
        },
        clearCostReductionOpportunitiesRegions: (state) => {
            state.costReductionOpportunitiesRegions = initialState.costReductionOpportunitiesRegions;
        },
        toggleCostOverTimeCostTypeIsSelected: (state, action: PayloadAction<string>) => {
            const costTypeName = action.payload;
            state.costOverTimeCostTypes.find(costType => costType.name === costTypeName)?.toggleIsSelected();
        },
        toggleCostReductionOpportunitiesCostTypeIsSelected: (state, action: PayloadAction<string>) => {
            const costTypeName = action.payload;
            state.costReductionOpportunitiesCostTypes.find(costType => costType.name === costTypeName)?.toggleIsSelected();
        },
        toggleCostReductionOpportunitiesStoreGroupsIsSelected: (state, action: PayloadAction<string>) => {
            const storeGroup = action.payload;
            state.costReductionOpportunitiesStoreGroups.find(sg => sg.name === storeGroup)?.toggleIsSelected();
        },
        toggleCostReductionOpportunitiesRegionsIsSelected: (state, action: PayloadAction<string>) => {
            const region = action.payload;
            state.costReductionOpportunitiesRegions.find(r => r.name === region)?.toggleIsSelected();
        },
        chooseAllCostOverTimeCostTypes: (state) => {
            state.costOverTimeCostTypes.forEach(costType => costType.setIsSelected(true));
        },
        chooseAllCostReductionOpportunitiesCostTypes: (state) => {
            state.costReductionOpportunitiesCostTypes.forEach(costType => costType.setIsSelected(true));
        },
        chooseAllCostReductionOpportunitiesStoreGroups: (state) => {
            state.costReductionOpportunitiesStoreGroups.forEach(costType => costType.setIsSelected(true));
        },
        chooseAllCostReductionOpportunitiesRegions: (state) => {
            state.costReductionOpportunitiesRegions.forEach(costType => costType.setIsSelected(true));
        },
        deselectAllCostOverTimeCostTypes: (state) => {
            state.costOverTimeCostTypes.forEach(costType => costType.setIsSelected(false));
        },
        deselectAllCostReductionOpportunitiesCostTypes: (state) => {
            state.costReductionOpportunitiesCostTypes.forEach(costType => costType.setIsSelected(false));
        },
        deselectAllCostReductionOpportunitiesStoreGroups: (state) => {
            state.costReductionOpportunitiesStoreGroups.forEach(storeGroup => storeGroup.setIsSelected(false));
        },
        deselectAllCostReductionOpportunitiesRegions: (state) => {
            state.costReductionOpportunitiesRegions.forEach(region => region.setIsSelected(false));
        },
        setCostMeasureType: (state, action: PayloadAction<CostMeasureType>) => {
            state.costMeasureType = action.payload;
        },
        clearCostMeasureType: (state) => {
            state.costMeasureType = initialState.costMeasureType;
        },
        setCostOverTimeGranularity: (state, action: PayloadAction<CostGranularity>) => {
            state.costOverTimeGranularity = action.payload;
        },
        clearCostOverTimeGranularity: (state) => {
            state.costOverTimeGranularity = initialState.costOverTimeGranularity;
        }
    },
    extraReducers: (builder: any) => {
        builder.addCase(loadCostOverview.pending, (state: CostOverviewState) => {
            state.isLoading = true;
            state.hasErrors = false;
            state.monthlyCosts = initialState.monthlyCosts;
            state.monthlyRevenue = initialState.monthlyRevenue;
            state.costOverTimeCostTypes = initialState.costOverTimeCostTypes;
            state.costReductionOpportunitiesCostTypes = initialState.costReductionOpportunitiesCostTypes;
            state.costReductionOpportunitiesStoreGroups = initialState.costReductionOpportunitiesStoreGroups;
            state.costReductionOpportunitiesRegions = initialState.costReductionOpportunitiesRegions;
        });
        builder.addCase(loadCostOverview.rejected, (state: CostOverviewState) => {
            state.isLoading = false;
            state.hasErrors = true;
            state.monthlyCosts = initialState.monthlyCosts;
            state.monthlyRevenue = initialState.monthlyRevenue;
            state.costOverTimeCostTypes = initialState.costOverTimeCostTypes;
            state.costReductionOpportunitiesCostTypes = initialState.costReductionOpportunitiesCostTypes;
            state.costReductionOpportunitiesStoreGroups = initialState.costReductionOpportunitiesStoreGroups;
            state.costReductionOpportunitiesRegions = initialState.costReductionOpportunitiesRegions;
        });
        builder.addCase(loadCostOverview.fulfilled, (state: CostOverviewState, action: PayloadAction<LoadCostOverviewResponse>) => {
            state.isLoading = false;
            state.hasErrors = false;
            state.monthlyCosts = action.payload.monthlyCosts;
            state.monthlyRevenue = action.payload.monthlyRevenue;
            state.costOverTimeCostTypes = action.payload.costOverTimeCostTypes;
            state.costReductionOpportunitiesCostTypes = action.payload.costReductionOpportunitiesCostTypes;
            state.costReductionOpportunitiesStoreGroups = action.payload.costReductionOpportunitiesStoreGroups;
            state.costReductionOpportunitiesRegions = action.payload.costReductionOpportunitiesRegions;
        });
    }
});

export const {
    toggleCostOverTimeCostTypeIsSelected,
    toggleCostReductionOpportunitiesCostTypeIsSelected,
    toggleCostReductionOpportunitiesStoreGroupsIsSelected,
    toggleCostReductionOpportunitiesRegionsIsSelected,
    chooseAllCostOverTimeCostTypes,
    chooseAllCostReductionOpportunitiesCostTypes,
    chooseAllCostReductionOpportunitiesStoreGroups,
    chooseAllCostReductionOpportunitiesRegions,
    deselectAllCostOverTimeCostTypes,
    setCostMeasureType,
    deselectAllCostReductionOpportunitiesCostTypes,
    deselectAllCostReductionOpportunitiesStoreGroups,
    deselectAllCostReductionOpportunitiesRegions,
    setCostOverTimeGranularity
} = costOverviewSlice.actions;

export const loadCostOverview = createAppAsyncThunk(
    "customer/insights/cost/costOverview/loadCostOverview",
    async (arg, thunkAPI) => {
        try {
            await thunkAPI.dispatch(setupCube());
            const state = thunkAPI.getState();

            const referenceDate = selectReferenceDate(state);
            const useCustomCalendar = selectUseCustomCalendar(state);
            const costTypes = selectCostTypes(state);
            const storeGroups = selectStoreGroups(state);
            const regions = selectRegions(state);
            const clientRegions = selectClientRegions(state);

            const monthlyCostsPromise = thunkAPI.dispatch(loadMonthlyCosts(referenceDate, useCustomCalendar));
            const monthlyRevenuePromise = thunkAPI.dispatch(loadMonthlyRevenue(referenceDate, useCustomCalendar));
            const results = await Promise.all([
                monthlyCostsPromise,
                monthlyRevenuePromise
            ]);
            const monthlyCosts = results[0];
            const monthlyRevenue = results[1];

            const costOverTimeCostTypes = costTypes.map(costType => {
                return new CostTypeMultiSelect(
                    costType,
                    true
                );
            });

            const costReductionOpportunitiesCostTypes = costTypes.map(costType => {
                return new CostTypeMultiSelect(
                    costType,
                    true
                );
            });

            const costReductionOpportunitiesStoreGroups = storeGroups.map(storeGroup => {
                return new StoreGroupMultiSelect(
                    storeGroup,
                    true
                );
            });

            const costReductionOpportunitiesRegions = clientRegions.filter(region => region.name !== "Blank").length > 0
                ? clientRegions.map(region => {
                    return new RegionMultiSelect(
                        region,
                        true
                    );
                })
                : regions.map(region => {
                    return new RegionMultiSelect(
                        region,
                        true
                    );
                });

            const loadCostResponse: LoadCostOverviewResponse = {
                monthlyCosts,
                monthlyRevenue,
                costOverTimeCostTypes,
                costReductionOpportunitiesCostTypes,
                costReductionOpportunitiesStoreGroups,
                costReductionOpportunitiesRegions
            };
            return loadCostResponse;
        }
        catch (error) {
            thunkAPI.dispatch(notifyError("Error loading CostOverview."));
            return thunkAPI.rejectWithValue(null);
        }
    }
);

export const clearCostOverview = (): AppThunk => (dispatch) => {
    dispatch(costOverviewSlice.actions.clearMonthlyCosts());
    dispatch(costOverviewSlice.actions.clearMonthlyRevenue());
    dispatch(costOverviewSlice.actions.clearCostOverTimeCostTypes());
    dispatch(costOverviewSlice.actions.clearCostMeasureType());
    dispatch(costOverviewSlice.actions.clearCostReductionOpportunitiesCostTypes());
    dispatch(costOverviewSlice.actions.clearCostReductionOpportunitiesStoreGroups());
    dispatch(costOverviewSlice.actions.clearCostReductionOpportunitiesRegions());
    dispatch(costOverviewSlice.actions.clearCostOverTimeGranularity());
};

export const selectIsLoading = (state: RootState) => {
    return state.customer.insights.cost.costOverview.isLoading;
};

export const selectHasErrors = (state: RootState) => {
    return state.customer.insights.cost.costOverview.hasErrors;
};

export const selectMonthlyCosts = (state: RootState) => {
    return state.customer.insights.cost.costOverview.monthlyCosts;
};

export const selectMonthlyRevenue = (state: RootState) => {
    return state.customer.insights.cost.costOverview.monthlyRevenue;
};

export const selectCostOverTimeCostTypes = (state: RootState) => {
    return state.customer.insights.cost.costOverview.costOverTimeCostTypes;
};

export const selectCostMeasureType = (state: RootState) => {
    return state.customer.insights.cost.costOverview.costMeasureType;
};

export const selectCostReductionOpportunitiesCostTypes = (state: RootState) => {
    return state.customer.insights.cost.costOverview.costReductionOpportunitiesCostTypes;
};

export const selectCostReductionOpportunitiesStoreGroups = (state: RootState) => {
    return state.customer.insights.cost.costOverview.costReductionOpportunitiesStoreGroups;
};

export const selectCostReductionOpportunitiesRegions = (state: RootState) => {
    return state.customer.insights.cost.costOverview.costReductionOpportunitiesRegions;
};

export const selectCostOverTimeGranularity = (state: RootState) => {
    return state.customer.insights.cost.costOverview.costOverTimeGranularity;
};

export const selectPastTwelveMonthsCosts = createSelector(
    (state: RootState) => selectReferenceDate(state),
    (state: RootState) => selectMonthlyCosts(state),
    (state: RootState) => selectUseCustomCalendar(state),
    (costReferenceDate, monthlyCosts, useCustomCalendar) => {
        if (!monthlyCosts) {
            return 0;
        }

        const oneYearPrior = useCustomCalendar ? { weeks: 52 } : { months: 12 };
        const twelveMonthsPriorReferenceDate = costReferenceDate.minus(oneYearPrior);

        return monthlyCosts.filter(costs => costs.date > twelveMonthsPriorReferenceDate)
            .reduce((total, item) => item.costValue + total, 0);
    }
);

export const selectPreviousYearCosts = createSelector(
    (state: RootState) => selectReferenceDate(state),
    (state: RootState) => selectMonthlyCosts(state),
    (state: RootState) => selectUseCustomCalendar(state),
    (costReferenceDate, monthlyCosts, useCustomCalendar) => {
        if (!monthlyCosts) {
            return 0;
        }

        const oneYearPrior = useCustomCalendar ? { weeks: 52 } : { months: 12 };
        const twoYearsPrior = useCustomCalendar ? { weeks: 52 * 2 } : { months: 12 * 2 };
        const twelveMonthsPriorReferenceDate = costReferenceDate.minus(oneYearPrior);
        const twentyFourMonthsPriorReferenceDate = costReferenceDate.minus(twoYearsPrior);

        return monthlyCosts.filter(costs => (costs.date > twentyFourMonthsPriorReferenceDate)
            && (costs.date <= twelveMonthsPriorReferenceDate))
            .reduce((total, item) => item.costValue + total, 0);
    }
);

export const selectLatestMonthAndPriorYearTotalCosts = createSelector(
    selectIsLoading,
    selectHasErrors,
    (state: RootState) => selectReferenceDate(state),
    (state: RootState) => selectMonthlyCosts(state),
    (state: RootState) => selectUseCustomCalendar(state),
    (isLoading, hasErrors, costsReferenceDate, monthlyCosts, hasCustomCalendar) => {
        interface LatestMonthAndPriorYearTotalCosts {
            latestMonthTotalCosts: number,
            latestMonthLabel: string,
            yearPriorTotalCosts: number,
            yearPriorLabel: string,
        }

        const latestMonthAndPriorYearTotalCosts: DataWrapper<LatestMonthAndPriorYearTotalCosts> = {
            isLoading: isLoading,
            hasErrors: hasErrors,
            data: {
                latestMonthTotalCosts: 0,
                latestMonthLabel: "",
                yearPriorTotalCosts: 0,
                yearPriorLabel: ""
            }
        };

        if (!costsReferenceDate || !monthlyCosts || monthlyCosts.length === 0 || latestMonthAndPriorYearTotalCosts.hasErrors || latestMonthAndPriorYearTotalCosts.isLoading) {
            return latestMonthAndPriorYearTotalCosts;
        }

        const mostRecentCost = maxBy(monthlyCosts, costItem => costItem.customDate.date.toMillis());
        const currentCustomDate = mostRecentCost?.customDate;
        const latestMonthTotalCosts = monthlyCosts.filter(costs => costs.date.toMillis() === currentCustomDate?.date.toMillis())
            .reduce((total, item) => item.costValue + total, 0);

        const priorYearTotalCosts = monthlyCosts
            .filter(costs => (costs.customDate.periodNumber === currentCustomDate?.periodNumber) && (costs.customDate.year === currentCustomDate.year - 1))
            .reduce((total, item) => item.costValue + total, 0);

        latestMonthAndPriorYearTotalCosts.data.latestMonthTotalCosts = latestMonthTotalCosts;
        latestMonthAndPriorYearTotalCosts.data.yearPriorTotalCosts = priorYearTotalCosts;

        // Labels
        const latestMonth = costsReferenceDate.startOf("month");
        const priorYearDate = costsReferenceDate.minus({ months: 12 }).startOf("month");
        const yearPriorCosts = monthlyCosts.find(costs => (costs.customDate.periodNumber === currentCustomDate?.periodNumber) && (costs.customDate.year === currentCustomDate.year - 1));
        latestMonthAndPriorYearTotalCosts.data.latestMonthLabel = hasCustomCalendar
            ? `${currentCustomDate?.dateLabel}`
            : `${latestMonth.monthLong} ${latestMonth.year}`;

        latestMonthAndPriorYearTotalCosts.data.yearPriorLabel = hasCustomCalendar
            ? `${yearPriorCosts?.customDate.dateLabel}`
            : `${priorYearDate.monthLong} ${priorYearDate.year}`;

        return latestMonthAndPriorYearTotalCosts;
    }
);

export const selectPastTwelveMonthsPercentageOfRevenue = createSelector(
    selectIsLoading,
    selectHasErrors,
    (state: RootState) => selectReferenceDate(state),
    (state: RootState) => selectMonthlyRevenue(state),
    (state: RootState) => selectPastTwelveMonthsCosts(state),
    (state: RootState) => selectPreviousYearCosts(state),
    (state: RootState) => selectUseCustomCalendar(state),
    (isLoading, hasErrors, costReferenceDate, monthlyRevenue, pastTwelveMonthsTotalCosts, previousYearTotalCosts, useCustomCalendar) => {
        interface PastTwelveMonthsPercentageOfRevenue {
            pastTwelveMonthsPercentage: number
            previousYearPercentage: number
            difference: number
        }

        const pastTwelveMonthsPercentageOfRevenue: DataWrapper<PastTwelveMonthsPercentageOfRevenue> = {
            isLoading: isLoading,
            hasErrors: hasErrors,
            data: {
                pastTwelveMonthsPercentage: 0,
                previousYearPercentage: 0,
                difference: 0
            }
        };

        if (!monthlyRevenue ||
            !pastTwelveMonthsTotalCosts ||
            !previousYearTotalCosts ||
            pastTwelveMonthsPercentageOfRevenue.hasErrors ||
            pastTwelveMonthsPercentageOfRevenue.isLoading) {
            return pastTwelveMonthsPercentageOfRevenue;
        }

        const oneYearPrior = useCustomCalendar ? { weeks: 52 } : { months: 12 };
        const twoYearsPrior = useCustomCalendar ? { weeks: 52 * 2 } : { months: 12 * 2 };

        const twelveMonthsPriorReferenceDate = costReferenceDate.minus(oneYearPrior);
        const pastTwelveMonthsRevenue = monthlyRevenue.filter(revenue => revenue.date > twelveMonthsPriorReferenceDate)
            .reduce((total, item) => item.revenue + total, 0);

        const twentyFourMonthsPriorReferenceDate = costReferenceDate.minus(twoYearsPrior);
        const yearPriorRevenue = monthlyRevenue.filter(revenue => (revenue.date <= twelveMonthsPriorReferenceDate)
            && (revenue.date > twentyFourMonthsPriorReferenceDate))
            .reduce((total, item) => item.revenue + total, 0);

        pastTwelveMonthsPercentageOfRevenue.data.pastTwelveMonthsPercentage = mathUtils.safePercentage(pastTwelveMonthsTotalCosts, pastTwelveMonthsRevenue);
        pastTwelveMonthsPercentageOfRevenue.data.previousYearPercentage = mathUtils.safePercentage(previousYearTotalCosts, yearPriorRevenue);
        pastTwelveMonthsPercentageOfRevenue.data.difference = pastTwelveMonthsPercentageOfRevenue.data.pastTwelveMonthsPercentage - pastTwelveMonthsPercentageOfRevenue.data.previousYearPercentage;

        return pastTwelveMonthsPercentageOfRevenue;
    }
);

export interface TotalCostOverTime {
    date: DateTime,
    customDate: CustomDate,
    group: string,
    costValue: number,
    revenueValue: number,
    costPercentageOfRevenue: number
}

export const selectTotalCostTrendsOverTime = createSelector(
    selectCostOverTimeCostTypes,
    selectCostOverTimeGranularity,
    (state: RootState) => selectMonthlyCosts(state),
    (state: RootState) => selectMonthlyRevenue(state),
    (costTypes, costOverTimeGranularity, monthlyCosts, monthlyRevenue) => {
        const selectedCostTypes = costTypes.filter(costType => costType.isSelected()).map(type => type.name);

        const storeGroupMonthlyCosts = _(monthlyCosts)
            .filter(costs => selectedCostTypes.includes(costs.costType))
            .groupBy(costOverTime => costOverTime.group)
            .mapValues((group, groupName) => {
                const groupRevenue = monthlyRevenue.filter(item => item.group === groupName);
                const monthlyTotalCostsOverTime = _(group).groupBy(costs => costs.customDate[costOverTimeGranularity])
                    .map((monthlyCosts, dateKey) => {
                        const earliestItem = _.minBy(monthlyCosts, item => item.date.toMillis()) ?? monthlyCosts[0];
                        const revenue = groupRevenue.filter(item => String(item.customDate[costOverTimeGranularity]) === dateKey);
                        const totalCosts = monthlyCosts.reduce((total, item) => item.costValue + total, 0);
                        const totalRevenue = revenue?.reduce((total, item) => item.revenue + total, 0);
                        return {
                            group: groupName,
                            date: earliestItem.date,
                            customDate: earliestItem.customDate,
                            costValue: totalCosts,
                            revenueValue: totalRevenue,
                            costPercentageOfRevenue: mathUtils.safePercentage(totalCosts, totalRevenue)
                        };
                    })
                    .sort((a, b) => numberSortExpression(a.customDate.date.toMillis(), b.customDate.date.toMillis(), SortDirection.ASC))
                    .value();
                return monthlyTotalCostsOverTime;
            })
            .value();

        return storeGroupMonthlyCosts;
    }
);

export const selectCostReductionOpportunity = createSelector(
    selectCostReductionOpportunitiesCostTypes,
    selectCostReductionOpportunitiesStoreGroups,
    selectCostReductionOpportunitiesRegions,
    (state: RootState) => selectClientRegions(state),
    (state: RootState) => selectCostReductionOpportunityByStore(state),
    (costReductionOpportunitiesCostTypes, costReductionOpportunitiesStoreGroups, costReductionOpportunitiesRegions, clientRegions, costReductionOpportunityByStore) => {
        const selectedCostTypes = costReductionOpportunitiesCostTypes.filter(costType => costType.isSelected()).map(type => type.name);
        const selectedStoreGroups = costReductionOpportunitiesStoreGroups.filter(storeGroup => storeGroup.isSelected()).map(type => type.name);
        const selectedRegions = costReductionOpportunitiesRegions.filter(region => region.isSelected()).map(type => type.name);
        
        return {
            isLoading: costReductionOpportunityByStore.isLoading,
            hasErrors: costReductionOpportunityByStore.hasErrors,
            data: _(costReductionOpportunityByStore.data)
                .filter(x => x.opportunityValue >= 0
                    && selectedCostTypes.includes(x.costName)
                    && selectedStoreGroups.includes(x.group ?? "")
                    && (
                        clientRegions.length === 1 && clientRegions.map(x => x.name).includes("Blank") ?
                        selectedRegions.includes(x.region ?? "") :
                        selectedRegions.includes(x.clientRegion ?? "")
                        
                    )
                )
                .groupBy(x => x.group).value()
        };
    }
);

export default costOverviewSlice;
